import React, { ChangeEvent, startTransition, useEffect, useState } from "react"
import { DateRange } from "react-date-range"
import format from "date-fns/format"
import addDays from "date-fns/addDays"
import { emitCustomEvent } from "react-custom-events"
import {
  HotelOptionsByCityType,
  hotelOptionsByCity,
} from "./../../utils/booking_engine"
import * as styles from "./calendar.module.scss"
import { CalendarPropsType, CalendarParamsType } from "./types"
import Button from "../button"
import useIsMobile from "../is_mobile"
import { toastMsg } from "../../utils/notifications"
import { join_paths, parseQueryParams, sp_to_qs } from "../../utils/url"
import {
  CachedSearchParamsType,
  cacheSearchParams,
  cachedSearchParams,
} from "../../utils/search"
import { AnimatePresence, motion } from "framer-motion"
import { MOVE_DOWN } from "../../utils/animation"
import { DEFAULT_SELECT_STYLES } from "../../utils/forms"

// Options defined at https://github.com/hypeserver/react-date-range

const Select = React.lazy(() => import("./../element/select"))
const Input = React.lazy(() => import("./../element/input"))

const Calendar = ({
  showCalendar,
  hotelPages,
  ...props
}: CalendarPropsType) => {
  const queryParams = parseQueryParams(props.location.search)
  const isMobile = useIsMobile()
  const [hotelOptions, setHotelOptions] = useState<
    HotelOptionsByCityType[] | null
  >(null)

  const getParam = (
    cache: CachedSearchParamsType | null,
    paramName: keyof CachedSearchParamsType,
    defaultValue: any | undefined
  ) => {
    // Return params from URL
    if (queryParams[paramName] !== undefined) {
      return queryParams[paramName]
    }
    // Return params from Cache
    if (cache) {
      return cache[paramName]
    }
    // Return property value
    if (props[paramName] !== undefined) {
      return props[paramName]
    }
    return defaultValue
  }

  const defaultSearchParams = () => {
    const cachedParams = cachedSearchParams()
    return {
      startDate: getParam(cachedParams, "check_in", new Date()),
      endDate: getParam(cachedParams, "check_out", addDays(new Date(), 1)),
      adult_count: getParam(cachedParams, "adult_count", 2),
      child_count: getParam(cachedParams, "child_count", 0),
      promo_codes: getParam(cachedParams, "promo_codes", []),
      rate_codes: getParam(cachedParams, "rate_codes", []),
    }
  }
  const [calendar, setCalendar] = useState<CalendarParamsType>({
    key: "selection",
    selected_hotel: props.selected_hotel ? props.selected_hotel : null,
    ...defaultSearchParams(),
  })

  useEffect(() => {
    if (hotelPages) {
      startTransition(() => setHotelOptions(hotelOptionsByCity(hotelPages)))
    }
  }, [hotelPages])

  return (
    <AnimatePresence>
      {showCalendar && (
        <motion.div
          className={`${styles.calendarWrapper} columns`}
          {...MOVE_DOWN}
        >
          <Button
            className="modal-close"
            aria-label="close"
            onClick={() => emitCustomEvent("close-calendar", calendar)}
          />
          <div className="column">
            {!!props.showHotels && !!props.showGuests && (
              <div className="cal-top-form">
                <div>
                  {!!props.showHotels && hotelOptions && (
                    <Select
                      className={"cal-sel1"}
                      name="cal_hotel"
                      label="Search"
                      placeholder="Select a hotel"
                      options={hotelOptions}
                      value={
                        calendar.selected_hotel
                          ? {
                              // @ts-ignore
                              label: hotelPages[calendar.selected_hotel].name,
                              value: calendar.selected_hotel,
                            }
                          : null
                      }
                      styles={{
                        ...DEFAULT_SELECT_STYLES,
                        groupHeading: (baseStyles: any) => ({
                          ...baseStyles,
                          textTransform: "none",
                          fontSize: "16px",
                          fontWeight: 600,
                        }),
                        option: (baseStyles: any, state: any) => {
                          return {
                            ...DEFAULT_SELECT_STYLES.option(baseStyles, state),
                            cursor: "pointer",
                            padding: "8px 12px 0 30px",
                          }
                        },
                        group: (baseStyles: any) => ({
                          ...baseStyles,
                          paddingBottom: "20px",
                        }),
                      }}
                      onChange={(option: any, _) =>
                        setCalendar({
                          ...calendar,
                          selected_hotel: option?.value,
                        })
                      }
                    />
                  )}
                </div>
                <div className="guests">
                  <div>
                    {!!props.showGuests && (
                      <Select
                        className={"cal-sel2"}
                        name="cal_adult"
                        label="Adults"
                        onChange={(option: any, _) =>
                          setCalendar({
                            ...calendar,
                            adult_count: option?.value,
                          })
                        }
                        value={{
                          label: `${calendar.adult_count}`,
                          value: `${calendar.adult_count}`,
                        }}
                        options={Array.from(
                          Array.from(Array(5).keys()),
                          (k, v) => ({
                            label: `${k + 1}`,
                            value: `${v + 1}`,
                          })
                        )}
                      />
                    )}
                  </div>
                  <div>
                    {!!props.showGuests && (
                      <Select
                        className={"cal-sel3"}
                        name="cal_child"
                        label="Children < 12"
                        onChange={(option: any, _) =>
                          setCalendar({
                            ...calendar,
                            child_count: option?.value,
                          })
                        }
                        value={{
                          label: `${calendar.child_count}`,
                          value: `${calendar.child_count}`,
                        }}
                        options={Array.from(
                          Array.from(Array(5).keys()),
                          (k, v) => ({
                            label: `${k}`,
                            value: `${v}`,
                          })
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            {(props.showDates === undefined || props.showDates) && (
              <DateRange
                showPreview={true}
                calendarFocus={"backwards"}
                minDate={props.minDate ? props.minDate : new Date()}
                maxDate={props.maxDate ? props.maxDate : undefined}
                editableDateInputs={false}
                months={isMobile ? 1 : 2} // Displays one month for mobile and two for the rest
                direction="horizontal"
                moveRangeOnFirstSelection={false}
                retainEndDateOnFirstSelection={true}
                preventSnapRefocus={true}
                onChange={(item: any) => {
                  // Added Hack to display next day when double click on same date
                  if (
                    item.selection.startDate.getTime() ===
                      item.selection.endDate.getTime() &&
                    item.selection.endDate.getTime() ===
                      calendar.startDate.getTime()
                  ) {
                    item.selection.endDate = addDays(
                      item.selection.startDate,
                      1
                    )
                  }
                  startTransition(() => setCalendar(item.selection))
                }}
                ranges={[calendar]}
              />
            )}
            {!props.showSummary && (
              <div className={"cal-btns"}>
                <button
                  className="cal-btn2 btn3"
                  onClick={() => emitCustomEvent("close-calendar", calendar)}
                >
                  Done
                </button>
              </div>
            )}
          </div>
          {props.showSummary && (
            <div className={"cal-col2 column "}>
              <h4>Your reservation</h4>
              <div>
                <span>Check-in</span>
                <span>{format(calendar.startDate, "MMMM dd, yyyy")}</span>
              </div>
              <div>
                <span>Check-out</span>
                <span>{format(calendar.endDate, "MMMM dd, yyyy")}</span>
              </div>
              <div>
                <span>Guests</span>
                <span>
                  {calendar.adult_count} Adult(s), {calendar.child_count}{" "}
                  Children
                </span>
              </div>
              <div>
                <span>Promo code</span>
                <span>
                  <Input
                    input_type="text"
                    input_name="cal_promo_code"
                    value={
                      calendar.promo_codes?.length
                        ? calendar.promo_codes[0]
                        : ""
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setCalendar({
                        ...calendar,
                        promo_codes: e.target.value ? [e.target.value] : [],
                      })
                    }
                  />
                </span>
              </div>
              {!!hotelPages && (
                <div>
                  <Button
                    className="cal-book-btn btn3"
                    onClick={() => {
                      if (!calendar.selected_hotel) {
                        return startTransition(() =>
                          toastMsg("Please choose location first", "error")
                        )
                      }
                      cacheSearchParams({
                        check_in: calendar.startDate,
                        check_out: calendar.endDate,
                        adult_count: calendar.adult_count,
                        child_count: calendar.child_count,
                        rate_codes: calendar.rate_codes,
                        promo_codes: calendar.promo_codes,
                      })
                      window.location.href = join_paths(
                        hotelPages[calendar.selected_hotel].link,
                        sp_to_qs(
                          calendar.startDate,
                          calendar.endDate,
                          calendar.adult_count,
                          calendar.child_count,
                          calendar.rate_codes,
                          calendar.promo_codes
                        )
                      )
                    }}
                  >
                    EXPLORE
                  </Button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Calendar
